import type { ClerkProvider } from '@clerk/clerk-react';
import type { ComponentProps } from 'react';

import type { ResolvedTheme } from '@core/hooks/useTheme';

/**
 * Derived from the provider's own props rather than imported by name.
 * `@clerk/clerk-react` does not re-export `Appearance`, and reaching into
 * `@clerk/types` would add an undeclared dependency on a package whose layout is
 * Clerk's to change.
 */
type Appearance = NonNullable<ComponentProps<typeof ClerkProvider>['appearance']>;

/**
 * Maps our design tokens onto Clerk's appearance API.
 *
 * Clerk's `variables` take concrete colour strings -- a `var(--x)` reference would be
 * captured once and never re-resolve -- so the computed values are read off the document
 * and the object is rebuilt whenever the theme flips. That is why this is a function of
 * `theme` rather than a constant.
 */
export function buildClerkAppearance(theme: ResolvedTheme): Appearance {
  const styles = getComputedStyle(document.documentElement);
  const rgb = (token: string) => `rgb(${styles.getPropertyValue(token).trim()})`;
  const isDark = theme === 'dark';

  return {
    // Wraps Clerk's stylesheet in @layer clerk, so our Tailwind utilities in `elements`
    // win on cascade order alone -- no !important anywhere.
    cssLayerName: 'clerk',
    variables: {
      colorPrimary: rgb('--c-accent'),
      colorBackground: 'transparent',
      colorForeground: rgb('--c-fg'),
      colorMutedForeground: rgb('--c-fg-muted'),
      // Solid surface, not a 4% wash: the card sits on glass, and a near-transparent
      // input on glass has no edge at all.
      colorInput: rgb('--c-surface'),
      colorInputForeground: rgb('--c-fg'),
      colorDanger: rgb('--c-danger'),
      colorSuccess: rgb('--c-success'),
      colorBorder: isDark ? 'rgb(255 255 255 / 0.22)' : 'rgb(0 0 0 / 0.16)',
      borderRadius: '14px',
      fontFamily: 'inherit',
      spacing: '1rem',
    },
    elements: {
      rootBox: 'w-full',
      // Clerk's own card is stripped so ours provides the surface.
      cardBox: 'w-full border-none bg-transparent shadow-none',
      card: 'bg-transparent p-0 shadow-none',
      headerTitle: 'text-[22px] font-semibold tracking-[-0.017em]',
      headerSubtitle: 'text-fg-muted text-[15px]',
      formButtonPrimary:
        'rounded-pill h-11 text-[15px] font-medium normal-case shadow-raised ' +
        'transition-transform duration-200 ease-spring active:scale-[0.97] ' +
        'motion-reduce:transition-none motion-reduce:active:scale-100',
      // Opaque surface + a real outline. The glass treatment disappeared against the
      // glass card behind it (white on white in light, grey on grey in dark).
      socialButtonsBlockButton:
        'rounded-pill h-11 bg-surface shadow-e1 ring-1 ring-inset ring-fg/15 hover:ring-fg/30',
      formFieldInput: 'bg-surface shadow-none ring-1 ring-inset ring-fg/15 focus:ring-2 focus:ring-accent',
      // The verification-code boxes are styled in styles/clerk-overrides.css: Clerk's
      // own rules beat utility classes on that input.
      // NOTE: `footer` is intentionally absent. Clerk 5.61 does not apply the `footer`
      // element key -- the rendered node carries no custom classes -- so it is handled
      // by an unlayered rule in styles/clerk-overrides.css instead. Re-check on upgrade.
      //
      // The sign-up link stays visible: Clerk owns account creation and our invite gates
      // what happens afterwards, so hiding it would strand a first-time user.
    },
  };
}
